// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useKeyboardShortcuts hook
// Registers global keyboard shortcuts for POS navigation and actions.
//
//   F1 -> Caisse screen
//   F2 -> Tables screen
//   F3 -> Reservations screen
//   F4 -> Open QR scanner
//   Escape -> Clear cart (on caisse screen)
//   Ctrl+Enter -> Validate cart and create transaction
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { useAppStore } from '@/store/app-store';
import { useCartStore } from '@/store/cart-store';
import { offlineQueue } from '@/services/offline-queue';

/**
 * Hook that registers keyboard shortcuts for the POS application.
 * Should be used once in the authenticated layout / root component.
 */
export function useKeyboardShortcuts(): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      const app = useAppStore.getState();
      const cart = useCartStore.getState();

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          app.setScreen('caisse');
          break;

        case 'F2':
          e.preventDefault();
          app.setScreen('tables');
          break;

        case 'F3':
          e.preventDefault();
          app.setScreen('reservations');
          break;

        case 'F4':
          e.preventDefault();
          app.setScannerOpen(true);
          break;

        case 'Escape':
          // Close scanner first if open
          if (app.scannerOpen) {
            app.setScannerOpen(false);
            break;
          }
          // Clear cart only when on the caisse screen, with confirmation if items exist
          if (app.screen === 'caisse' && cart.items.length > 0) {
            if (window.confirm('Vider le panier ?')) {
              cart.clear();
              app.showToast('Panier vide', 'info');
            }
          }
          break;

        case 'Enter':
          if (e.ctrlKey) {
            e.preventDefault();
            handleValidateCart();
          }
          break;

        default:
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
}

// ---------------------------------------------------------------------------
// Cart validation helper
// ---------------------------------------------------------------------------

async function handleValidateCart(): Promise<void> {
  const cart = useCartStore.getState();

  // Must have items and at least one payment
  if (cart.items.length === 0) return;

  try {
    const transaction = cart.buildTransaction();
    if (!transaction) return;

    // Enqueue for sync (works both online and offline)
    await offlineQueue.enqueueTransaction(transaction);

    // Clear cart after successful enqueue
    cart.clear();

    // Show success toast
    useAppStore.getState().showToast('Transaction enregistree', 'success');
  } catch (error) {
    console.error('[KeyboardShortcuts] Cart validation error:', error);
    useAppStore
      .getState()
      .showToast('Erreur validation: ' + (error as Error).message, 'error');
  }
}
